import type {
  AssistantMessage,
  AssistantPhase,
  ChannelId,
  IntakePending,
  Operator,
  RatingState,
  ServerMessage,
  SessionMode,
} from './types';

/**
 * Session-persistence layer — bewaart de sessie-id in localStorage zodat een
 * pagina-refresh geen lopende chat verliest.
 *
 * SECURITY TRADE-OFF (bewust, voor v1 interne demo):
 *   localStorage is leesbaar door élk JavaScript in dezelfde origin,
 *   inclusief eventuele XSS-injectie op de host-site. Dit stelt de GUID
 *   bloot aan exfiltratie. Voor productie migreren we naar HttpOnly
 *   cookie-auth (zie docs/architecture.md §3 + task #7).
 *
 * TTL: 12 uur. Als een lokaal opgeslagen sessie ouder is, droppen we hem
 * preventief zonder de server te bellen (vermijdt zinloze 404-requests).
 */

/**
 * Gedeeld prefix voor alle localStorage-keys van het widget. Bij een
 * rename hoeft alleen dit te wijzigen — alle keys volgen automatisch.
 */
const STORAGE_PREFIX = 'embrace-chat:';
export const SESSION_KEY = `${STORAGE_PREFIX}session` as const;
export const VISITOR_KEY = `${STORAGE_PREFIX}visitor` as const;
export const AAVA_SESSION_KEY = `${STORAGE_PREFIX}aava` as const;
const DRAFT_KEY = `${STORAGE_PREFIX}draft` as const;
const PRIVACY_HINT_SHOWN_KEY = `${STORAGE_PREFIX}privacy-hint-shown` as const;
/**
 * Aparte panel-open-voorkeur. Staat los van de session-snapshot zodat
 * de open/dicht-keuze ook wordt bewaard wanneer er nog géén sessie is
 * (bezoeker opent paneel op een marketing-pagina, navigeert door de
 * site, paneel blijft open).
 */
const PANEL_PREF_KEY = `${STORAGE_PREFIX}panel-pref` as const;
/**
 * Pre-session state-snapshot — kanaal-keuze, bot-flow, conversation
 * voordat een echte session-id is aangemaakt. Zonder dit verliest de
 * bezoeker zijn input bij elke pagina-navigatie. Wordt gewist zodra
 * een echte session start (dan neemt SESSION_KEY het over) of bij
 * SESSION_RESET.
 */
const PRE_SESSION_KEY = `${STORAGE_PREFIX}pre-session` as const;
const PRE_SESSION_TTL_MS = 24 * 60 * 60 * 1000;
const TTL_MS = 12 * 60 * 60 * 1000; // 12 uur

/**
 * Schema-versie van de PersistedSession-payload. Verhoog bij elke
 * incompatibele wijziging zodat oudere snapshots van vorige rollouts
 * gericht gedropt of gemigreerd kunnen worden. Missing version in
 * geladen JSON wordt behandeld als v1 (initial release).
 */
const CURRENT_SCHEMA_VERSION = 1;

/**
 * Schema-versie van PersistedVisitor. Apart van CURRENT_SCHEMA_VERSION
 * zodat visitor en session onafhankelijk kunnen evolueren.
 */
const CURRENT_VISITOR_SCHEMA_VERSION = 1;

/**
 * Volledige snapshot van een geëindigd gesprek — bewaard op het moment dat
 * SESSION_ENDED dispatched wordt. Reden: de server kan na /leave de sessie
 * opruimen (404 op poll), waardoor we anders het gesprek + transcript-
 * download zouden verliezen bij een refresh. Met deze snapshot kunnen we
 * de ended-state direct hydrateren zonder server-roundtrip.
 */
export type EndedSnapshot = {
  serverMessages: ServerMessage[];
  assistantMessages: AssistantMessage[];
  operator: Operator | null;
  endedAt: number;
  reason: 'customer' | 'server';
  /**
   * Session-id van de afgesloten conversatie. Optioneel voor backwards-
   * compat met snapshots van vóór de rating-feature. Nodig om na refresh
   * nog een POST /rate te kunnen uitvoeren.
   */
  sessionId?: string | null;
  /**
   * Persistente rating-state. Optioneel: oude snapshots zonder dit veld
   * resulteren in een non-rateable ended-state (geen UI getoond).
   */
  rating?: RatingState | null;
};

/**
 * Snapshot van een lopend `phase: 'active'`-gesprek. Wordt geschreven zodra
 * de operator gekoppeld is en bij elke serverMessages-update. Doel: bij een
 * pagina-refresh kan de UI direct in active-state hydrateren in plaats van
 * eerst via 'queued' naar 'active' te transitioneren — dat zou bij refresh
 * een ongewenste searching-banner-flash + composer-slide-animatie geven
 * voor een gesprek dat al lang loopt.
 *
 * Aanwezigheid impliceert dat de gepersisteerde session in active-state
 * was; bij ended wordt 'm gewist (de endedSnapshot neemt het over).
 */
export type ActiveSnapshot = {
  operator: Operator;
  serverMessages: ServerMessage[];
};

export type PersistedSession = {
  /** Schema-versie zodat toekomstige rollouts incompatibele snapshots
   *  gericht kunnen droppen. Optioneel voor backwards-compat met
   *  pre-v1 snapshots zonder dit veld. */
  schemaVersion?: number;
  id: string;
  mode: SessionMode;
  /**
   * Of het paneel open was op het moment van laatste save. Op bootstrap
   * resumen we de panel-open-state alleen als er ook een geldige sessie
   * is — voorkomt een leeg paneel dat na expiry/reset blijft openstaan.
   */
  panelOpen: boolean;
  /**
   * Hoogste operator-message-timestamp die de bezoeker theoretisch al heeft
   * gezien. Wordt door de unread-badge gebruikt om te bepalen welke
   * operator-messages ongelezen zijn na een refresh.
   */
  lastReadAt: number;
  /**
   * Bot-Q&A en system-pills uit het assistant-tijdvak (greeting, naam-
   * vraag, e-mail-vraag, "operator gekoppeld"-pill). Niet onderdeel van
   * de echte chat-thread — server kent ze niet. Persisten zodat de
   * bezoeker ze terugziet na een refresh, en zodat het transcript
   * compleet is. Optioneel voor backwards-compat met snapshots van
   * vóór deze rollout.
   */
  assistantMessages?: AssistantMessage[];
  /** Volledige conversatie-snapshot zodra het gesprek geëindigd is. */
  endedSnapshot: EndedSnapshot | null;
  /**
   * Snapshot van een actief gesprek (operator-attached). Optioneel voor
   * backwards-compat met snapshots van vóór deze rollout — als afwezig,
   * valt bootstrap terug op de queued-flow + polling-attach.
   */
  activeSnapshot?: ActiveSnapshot | null;
  savedAt: number;
};

/**
 * Parse + valideer een raw localStorage-payload naar een PersistedSession.
 * Retourneert `null` bij invalid JSON, ontbrekende verplichte velden,
 * incompatibele schemaVersion, of verlopen TTL. Geen storage-side-effects
 * (verschilt van `loadPersistedSession`, die bij invalid/expired ook de
 * storage cleart).
 *
 * Bedoeld voor cross-tab `storage`-events: daar krijgen we de raw string
 * uit `e.newValue` en willen we 'm valideren met dezelfde regels als
 * onze normale load.
 */
export function parsePersistedSession(raw: string): PersistedSession | null {
  let parsed: Partial<PersistedSession>;
  try {
    parsed = JSON.parse(raw) as Partial<PersistedSession>;
  } catch {
    return null;
  }
  if (!parsed || typeof parsed.id !== 'string' || !parsed.id) return null;
  if (parsed.mode !== 'anonymous' && parsed.mode !== 'authenticated') return null;
  if (typeof parsed.savedAt !== 'number') return null;
  // Schema-version-check: missing wordt geïnterpreteerd als v1 (initial),
  // hogere versies gedropt zodat een rollback geen toekomstige shapes
  // probeert te lezen.
  const version = parsed.schemaVersion ?? 1;
  if (version > CURRENT_SCHEMA_VERSION) return null;
  if (Date.now() - parsed.savedAt > TTL_MS) return null;

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: parsed.id,
    mode: parsed.mode,
    // Backwards-compat: oudere snapshots zonder deze velden → defaults.
    panelOpen: parsed.panelOpen === true,
    lastReadAt:
      typeof parsed.lastReadAt === 'number' ? parsed.lastReadAt : 0,
    assistantMessages: Array.isArray(parsed.assistantMessages)
      ? parsed.assistantMessages
      : undefined,
    endedSnapshot: parsed.endedSnapshot ?? null,
    // ActiveSnapshot is optioneel + nieuw; oude snapshots hebben dit niet.
    // We accepteren alleen objecten met de juiste shape; rommel = null.
    activeSnapshot: isValidActiveSnapshot(parsed.activeSnapshot)
      ? parsed.activeSnapshot
      : null,
    savedAt: parsed.savedAt,
  };
}

function isValidActiveSnapshot(value: unknown): value is ActiveSnapshot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ActiveSnapshot>;
  return (
    !!v.operator &&
    typeof v.operator === 'object' &&
    typeof (v.operator as Operator).name === 'string' &&
    Array.isArray(v.serverMessages)
  );
}

export function loadPersistedSession(): PersistedSession | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SESSION_KEY);
  } catch {
    // Incognito / blocked / quota → geen persistence, geen drama
    return null;
  }
  if (!raw) return null;

  const parsed = parsePersistedSession(raw);
  if (!parsed) {
    // Corrupted of verlopen JSON — gooi hem weg.
    clearPersistedSession();
    return null;
  }
  return parsed;
}

export function savePersistedSession(
  id: string,
  mode: SessionMode,
  panelOpen: boolean,
  lastReadAt: number,
  endedSnapshot: EndedSnapshot | null = null,
  assistantMessages: AssistantMessage[] | undefined = undefined,
  activeSnapshot: ActiveSnapshot | null = null,
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload: PersistedSession = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      id,
      mode,
      panelOpen,
      lastReadAt,
      assistantMessages,
      endedSnapshot,
      activeSnapshot,
      savedAt: Date.now(),
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  } catch {
    // Quota full / blocked → sessie werkt nog binnen deze tab, alleen geen resume
  }
}

/**
 * Update alleen de panelOpen-flag van de bestaande snapshot. Geen-op als er
 * geen sessie is (bezoeker minimaliseert paneel zonder lopende chat → niks
 * om te bewaren).
 */
export function updatePersistedPanelOpen(panelOpen: boolean): void {
  // Standalone pref blijft altijd up-to-date — ook zonder lopende sessie.
  // Bezoekers die op marketing-pagina's de chat openen, hebben anders bij
  // navigatie weer een dicht paneel.
  savePanelOpenPref(panelOpen);
  const existing = loadPersistedSession();
  if (!existing) return;
  savePersistedSession(
    existing.id,
    existing.mode,
    panelOpen,
    existing.lastReadAt,
    existing.endedSnapshot,
    existing.assistantMessages,
    existing.activeSnapshot,
  );
}

export function savePanelOpenPref(open: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PANEL_PREF_KEY, open ? 'true' : 'false');
  } catch { /* quota / disabled — niet kritiek */ }
}

export function loadPanelOpenPref(): boolean | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PANEL_PREF_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch { return null; }
}

export function updatePersistedLastReadAt(lastReadAt: number): void {
  const existing = loadPersistedSession();
  if (!existing) return;
  savePersistedSession(
    existing.id,
    existing.mode,
    existing.panelOpen,
    lastReadAt,
    existing.endedSnapshot,
    existing.assistantMessages,
    existing.activeSnapshot,
  );
}

export function updatePersistedEndedSnapshot(snapshot: EndedSnapshot): void {
  const existing = loadPersistedSession();
  if (!existing) return;
  // Bij overgang naar ended wist activeSnapshot — endedSnapshot neemt
  // het over en we willen geen stale operator/serverMessages laten staan
  // die bootstrap zou kunnen interpreteren.
  savePersistedSession(
    existing.id,
    existing.mode,
    existing.panelOpen,
    existing.lastReadAt,
    snapshot,
    existing.assistantMessages,
    null,
  );
}

/**
 * Update de assistant-Q&A snapshot. Geen-op als er geen lopende sessie is —
 * tijdens bot-flow is er nog geen session-id en zit visitor in een aparte
 * VISITOR_KEY-store.
 */
export function updatePersistedAssistantMessages(
  assistantMessages: AssistantMessage[],
): void {
  const existing = loadPersistedSession();
  if (!existing) return;
  savePersistedSession(
    existing.id,
    existing.mode,
    existing.panelOpen,
    existing.lastReadAt,
    existing.endedSnapshot,
    assistantMessages,
    existing.activeSnapshot,
  );
}

/**
 * Schrijf of vervang de active-snapshot. Geen-op als er geen lopende
 * sessie is — gebruikt na SESSION_AGENT_ATTACHED en bij elke
 * serverMessages-mutatie zolang phase='active'.
 */
export function updatePersistedActiveSnapshot(snapshot: ActiveSnapshot): void {
  const existing = loadPersistedSession();
  if (!existing) return;
  savePersistedSession(
    existing.id,
    existing.mode,
    existing.panelOpen,
    existing.lastReadAt,
    existing.endedSnapshot,
    existing.assistantMessages,
    snapshot,
  );
}

export function clearPersistedSession(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

// =============================================================================
//  Composer-draft — losstaande key zodat een draft kan bestaan tijdens bot-flow
//  (nog geen sessie) én tijdens een actief gesprek.
// =============================================================================

export function loadDraft(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

export function saveDraft(text: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (text) {
      localStorage.setItem(DRAFT_KEY, text);
    } else {
      localStorage.removeItem(DRAFT_KEY);
    }
  } catch {
    /* quota of incognito — geen drama */
  }
}

export function clearDraft(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

// =============================================================================
//  Visitor — onthoudt naam + e-mail tussen bezoeken
// =============================================================================

/**
 * Persistente visitor-data. Wordt opgeslagen op INTAKE_SUBMITTED, gelezen
 * op bootstrap. Zonder TTL — visitor heeft geen server-counterpart die
 * kan expiren, dus alleen explicit reset wist 'm.
 *
 * SECURITY: zelfde tradeoff als SESSION_KEY (zie file-header) — XSS op
 * de host kan dit lezen. Voor productie zou cookie-auth of een server-
 * side identity-store de juiste oplossing zijn (parked op task #7/#9).
 */
export type PersistedVisitor = {
  schemaVersion: number;
  firstName: string;
  email: string;
  savedAt: number;
};

export function parsePersistedVisitor(raw: string): PersistedVisitor | null {
  let parsed: Partial<PersistedVisitor>;
  try {
    parsed = JSON.parse(raw) as Partial<PersistedVisitor>;
  } catch {
    return null;
  }
  if (!parsed) return null;
  if (typeof parsed.firstName !== 'string' || !parsed.firstName) return null;
  if (typeof parsed.email !== 'string' || !parsed.email) return null;
  if (typeof parsed.savedAt !== 'number') return null;
  // Toekomst-veiligheid: hogere versies droppen voor rollback-safety.
  const version = parsed.schemaVersion ?? 1;
  if (version > CURRENT_VISITOR_SCHEMA_VERSION) return null;
  return {
    schemaVersion: CURRENT_VISITOR_SCHEMA_VERSION,
    firstName: parsed.firstName,
    email: parsed.email,
    savedAt: parsed.savedAt,
  };
}

export function loadPersistedVisitor(): PersistedVisitor | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(VISITOR_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  const parsed = parsePersistedVisitor(raw);
  if (!parsed) {
    // Corrupted — gooi 'm weg zodat een fresh-start mogelijk is.
    clearPersistedVisitor();
    return null;
  }
  return parsed;
}

export function savePersistedVisitor(input: {
  firstName: string;
  email: string;
}): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload: PersistedVisitor = {
      schemaVersion: CURRENT_VISITOR_SCHEMA_VERSION,
      firstName: input.firstName,
      email: input.email,
      savedAt: Date.now(),
    };
    localStorage.setItem(VISITOR_KEY, JSON.stringify(payload));
  } catch {
    /* quota of incognito — geen drama, visitor wordt volgende keer opnieuw gevraagd */
  }
}

export function clearPersistedVisitor(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(VISITOR_KEY);
  } catch {
    /* ignore */
  }
}

// =============================================================================
//  Privacy-disclaimer "shown"-flag — toont onehelpfully eenmalig na eerste opslag
// =============================================================================

export function markPrivacyHintShown(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PRIVACY_HINT_SHOWN_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isPrivacyHintShown(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(PRIVACY_HINT_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

// =============================================================================
//  AAVA-intake-sessie — lichtgewicht persistentie van conversation-anker
// =============================================================================
//
// Bewust alleen `conversationId` + `contractId` bewaard, NIET de hele
// transcript. Op page-refresh hydraten we naar deze twee waarden, halen
// vervolgens de transcript via `GET /v1/conversation/{id}` op (server is
// source-of-truth voor messages, beter dan elk message ook lokaal te
// kopiëren).
//
// TTL: 24 uur. Een conversation die ouder is dan een dag is vrijwel
// zeker niet meer zinvol — en de server kan 'm sowieso al hebben
// opgeruimd. Bij stale state vallen we netjes terug op een fresh
// channel-pick.

export type PersistedAavaSession = {
  conversationId: string;
  contractId: string;
  /**
   * Volledige snapshot van `assistantMessages` op moment van saven.
   * Includes de lokaal-only UI-messages (welkom-prompt, contract-keuze)
   * die NIET in de server-transcript zitten. Bij refresh restoren we
   * deze lijst direct — geen extra server-roundtrip nodig en geen
   * dedupe-puzzel met `GET /messages`-output.
   *
   * `dataUri` op image-files wordt gestript voor persistentie (kost
   * megabytes; localStorage-quota is ~5MB). Bij restore tonen we image-
   * attachments als placeholder i.p.v. echte thumb.
   */
  messages: PersistedAssistantMessage[];
  savedAt: number;
};

/**
 * Persisted-variant van AssistantMessage. `dataUri` is optioneel: bij
 * normale write blijft 'm intact zodat foto's na page-refresh terugkomen.
 * Wanneer de payload de localStorage-quota overschrijdt, herschrijven we
 * dezelfde sessie met `dataUri` weggestript (zie `saveAavaSession`).
 */
export type PersistedAssistantMessage = {
  id: string;
  author: 'assistant' | 'customer' | 'system';
  text: string;
  timestamp: number;
  /** Server-ordinal van een AAVA-reply — bewaard zodat de poll-dedup ook
   * na een page-refresh (rehydratie) blijft werken. */
  aavaMessageId?: number;
  files?: Array<{
    id: string;
    name: string;
    sizeLabel: string;
    kind: 'image' | 'pdf' | 'file';
    dataUri?: string;
  }>;
};

const AAVA_TTL_MS = 24 * 60 * 60 * 1000;

export function saveAavaSession(data: {
  conversationId: string;
  contractId: string;
  messages: PersistedAssistantMessage[];
}): void {
  if (typeof localStorage === 'undefined') return;
  const base = {
    conversationId: data.conversationId,
    contractId: data.contractId,
    savedAt: Date.now(),
  };
  // Probeer eerst met volledige messages (incl. foto-dataUris). Bij
  // quota-overflow vallen we terug op een variant zonder dataUris —
  // dan komen tekst-context en metadata wel terug na refresh, maar
  // foto's vallen weg. Beter dan helemaal niets opslaan.
  const fullResult = trySaveAava({ ...base, messages: data.messages });
  if (fullResult === 'ok' || fullResult === 'fatal') return;
  trySaveAava({ ...base, messages: data.messages.map(stripDataUris) });
}

function trySaveAava(payload: PersistedAavaSession): 'ok' | 'quota' | 'fatal' {
  try {
    localStorage.setItem(AAVA_SESSION_KEY, JSON.stringify(payload));
    return 'ok';
  } catch (err) {
    return isQuotaError(err) ? 'quota' : 'fatal';
  }
}

function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  // DOMException-namen variëren per browser: Chrome/Safari =
  // "QuotaExceededError", Firefox-pre-100 = "NS_ERROR_DOM_QUOTA_REACHED",
  // legacy IE = code 22.
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    (err as { code?: number }).code === 22
  );
}

function stripDataUris(m: PersistedAssistantMessage): PersistedAssistantMessage {
  if (!m.files?.some((f) => f.dataUri)) return m;
  return {
    ...m,
    files: m.files.map(({ dataUri: _drop, ...rest }) => rest),
  };
}

/**
 * Converteer een in-memory `AssistantMessage` naar de Persisted-variant.
 * Behoudt `dataUri` op image-files zodat foto's na page-refresh terugkomen;
 * `saveAavaSession` strippt 'm pas bij quota-overflow.
 */
export function toPersistedMessage(m: AssistantMessage): PersistedAssistantMessage {
  return {
    id: m.id,
    author: m.author,
    text: m.text,
    timestamp: m.timestamp,
    ...(m.aavaMessageId !== undefined ? { aavaMessageId: m.aavaMessageId } : {}),
    ...(m.files ? { files: m.files.map(toPersistedFile) } : {}),
  };
}

function toPersistedFile(
  f: NonNullable<AssistantMessage['files']>[number],
): NonNullable<PersistedAssistantMessage['files']>[number] {
  return {
    id: f.id,
    name: f.name,
    sizeLabel: f.sizeLabel,
    kind: f.kind,
    ...(f.dataUri ? { dataUri: f.dataUri } : {}),
  };
}

export function loadAavaSession(): PersistedAavaSession | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null;
  try {
    raw = localStorage.getItem(AAVA_SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Partial<PersistedAavaSession>;
    if (
      typeof obj.conversationId !== 'string' ||
      typeof obj.contractId !== 'string' ||
      typeof obj.savedAt !== 'number'
    ) {
      return null;
    }
    // Stale-check: oudere snapshots negeren en wissen.
    if (Date.now() - obj.savedAt > AAVA_TTL_MS) {
      clearAavaSession();
      return null;
    }
    return {
      conversationId: obj.conversationId,
      contractId: obj.contractId,
      // Backwards-compat: oudere snapshots zonder messages-veld krijgen
      // een lege lijst (preamble komt dan helaas niet terug — gebruiker
      // moet opnieuw beginnen voor die specifieke refresh).
      messages: Array.isArray(obj.messages) ? obj.messages : [],
      savedAt: obj.savedAt,
    };
  } catch {
    return null;
  }
}

export function clearAavaSession(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(AAVA_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

// =============================================================================
//  Pre-session state (kanaal-keuze, bot-flow, conversation) — los van de
//  session-snapshot zodat de bezoeker bij elke pagina-navigatie z'n
//  voortgang terugziet. Wordt automatisch gewist zodra een echte sessie
//  start (SESSION_STARTED, HYDRATE_ACTIVE) of bij SESSION_RESET.
// =============================================================================

export type PersistedPreSession = {
  pickedChannel: ChannelId | null;
  pickerStep: 'root' | 'chat-list';
  intake: IntakePending | null;
  assistant: AssistantPhase | null;
  assistantMessages: AssistantMessage[];
  savedAt: number;
};

export function savePreSession(data: Omit<PersistedPreSession, 'savedAt'>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload: PersistedPreSession = { ...data, savedAt: Date.now() };
    localStorage.setItem(PRE_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* quota / disabled — niet kritiek */
  }
}

export function loadPreSession(): PersistedPreSession | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null;
  try { raw = localStorage.getItem(PRE_SESSION_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Partial<PersistedPreSession>;
    if (
      typeof obj.savedAt !== 'number' ||
      Date.now() - obj.savedAt > PRE_SESSION_TTL_MS
    ) {
      clearPreSession();
      return null;
    }
    return {
      pickedChannel: (obj.pickedChannel ?? null) as ChannelId | null,
      pickerStep: obj.pickerStep === 'chat-list' ? 'chat-list' : 'root',
      intake: obj.intake ?? null,
      assistant: obj.assistant ?? null,
      assistantMessages: Array.isArray(obj.assistantMessages) ? obj.assistantMessages : [],
      savedAt: obj.savedAt,
    };
  } catch {
    return null;
  }
}

export function clearPreSession(): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(PRE_SESSION_KEY); } catch { /* ignore */ }
}
